import { useRef, useState } from "react";
import { Ban, CircleCheck, Maximize2, CircleHelp, FileX2, ListChecks, LoaderCircle, RotateCcw, SquareFunction, TrendingUp, TriangleAlert } from "lucide-react";
import type { Segnalazione } from "@/api/analisiBolletta";
import { type Mostrato, type RigaVoce, vociDaMostrare } from "@/lib/vociBolletta";
import {
  AI_ERRORE_GAS, avvisoLuce, circa, cifreVoce, ESTESA_APRI, euro, GIUDIZIO, motivoDettaglio, motivoFuoriPosto, nomeVoce, prezzoVoce, VOCI_BOTTONE, VOCI_ERRORE, VOCI_IN_CORSO,
  testoPerche, VOCI_NESSUNA, VOCI_NOTA, VOCI_RIPROVA, VOCI_TITOLO,
} from "@/lib/analisiScritte";
import type { VoceAnalisi } from "./useAnalisiChat";
import { ModaleBolletta } from "./ModaleBolletta";
import { Perche } from "./Perche";
import { useVistaCliente } from "./vistaCliente";

// giudizio sempre in parole e icona, mai solo col colore (ui-ux-pro-max: color-not-only)
const STILE: Record<Mostrato, { icona: typeof TrendingUp; classi: string }> = {
  G: { icona: TrendingUp, classi: "text-red-800 bg-red-50 border-red-200" },
  N: { icona: Ban, classi: "text-blue-900 bg-blue-50 border-blue-200" },
  // colore di «N» (stessa gravità), icona sua: la formula (21/10)
  V: { icona: SquareFunction, classi: "text-blue-900 bg-blue-50 border-blue-200" },
  P: { icona: CircleHelp, classi: "text-amber-800 bg-amber-50 border-amber-200" },
};

function Dettagli({ s }: { s: Segnalazione }) {
  // prezzo e valore regolato con gli stessi decimali: una gonfiata non mostra mai due numeri uguali
  const [cifra, regolato] = s.valore && s.riferimento ? cifreVoce(s.valore, s.riferimento) : [undefined, null];
  const prezzo = s.valore && s.unita ? prezzoVoce(s.valore, s.unita, cifra) : null;
  if (s.giudizio === "G") {
    return (
      <>
        {prezzo && regolato && (
          <p className="text-xs text-muted-foreground tabular-nums mt-0.5">
            {prezzo} · regolato {regolato}
          </p>
        )}
        {s.euro_in_piu && (
          <p className="text-xs tabular-nums mt-0.5">{circa(s.stimato)}<b>{euro(s.euro_in_piu)}</b> in più in questa bolletta</p>
        )}
      </>
    );
  }
  // quanto pesa in bolletta una voce che potrebbe non esserci o non dovrebbe esserci (Alfonso 17/10)
  const peso = s.euro_in_piu ?? (s.valore && !s.unita ? s.valore : null);
  if (s.giudizio === "V") return null;      // nessuna cifra: i nomi stanno nel «Perché»
  if (s.giudizio === "N") {
    const motivo = motivoFuoriPosto(s.voce);
    return (
      <>
        {motivo && <p className="text-xs text-muted-foreground mt-0.5">{motivo}</p>}
        {peso && <p className="text-xs tabular-nums mt-0.5"><b>{euro(peso)}</b> in questa bolletta</p>}
      </>
    );
  }
  return (      // P
    <>
      {prezzo && <p className="text-xs text-muted-foreground tabular-nums mt-0.5">{prezzo}</p>}
      {peso && <p className="text-xs tabular-nums mt-0.5"><b>{euro(peso)}</b> in questa bolletta</p>}
    </>
  );
}

function Riga({ riga }: { riga: RigaVoce }) {
  const { prima: s, altre } = riga;
  // una sola etichetta, la più forte (Alfonso 19/10: «Non dovrebbe esserci» e «Potrebbe non esserci» insieme sembrano
  // in contraddizione); i motivi delle altre restano nel «Perché»
  const giudizi = [s.giudizio];
  return (
    <li className="px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium">{nomeVoce(s.nome || s.dicitura)}</p>
        <div className="shrink-0 flex flex-col items-end gap-1">
          {giudizi.map((g) => {
            const { icona: Icona, classi } = STILE[g];
            return (
              <span key={g} className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-1.5 py-0.5 ${classi}`}>
                <Icona className="w-3 h-3" aria-hidden="true" />
                {GIUDIZIO[g]}
              </span>
            );
          })}
        </div>
      </div>
      <Dettagli s={s} />
      <Perche testo={testoPerche([s, ...altre].map((x) => x.descrizione).filter(Boolean).join(" ") || null,
                                 s.stimato ? s.quota_picco : null, s.mancanti)} />
    </li>
  );
}

function Corpo({ voce }: { voce: VoceAnalisi }) {
  const stato = voce.dettaglioStato;
  const cliente = useVistaCliente();
  if (stato === "in_corso") {
    return (
      <div className="space-y-2" aria-busy="true">
        <p className="text-sm text-muted-foreground flex items-start gap-2" role="status">
          <LoaderCircle className="w-4 h-4 mt-0.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {VOCI_IN_CORSO}
        </p>
        <div className="bg-card rounded-lg border border-border p-3 space-y-2.5" aria-hidden="true">
          <div className="h-4 w-3/4 rounded bg-border/60 animate-pulse motion-reduce:animate-none" />
          <div className="h-3 w-1/2 rounded bg-border/60 animate-pulse motion-reduce:animate-none" />
          <div className="h-4 w-2/3 rounded bg-border/60 animate-pulse motion-reduce:animate-none mt-3" />
          <div className="h-3 w-2/5 rounded bg-border/60 animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    );
  }
  if (stato === "su_richiesta") {
    if (!voce.controlla) return null;      // percorso QR: la fase 2 parte da sola, niente bottone
    return (
      <button
        type="button"
        onClick={voce.controlla}
        className="w-full min-h-[44px] rounded-lg border border-brand text-brand-dark font-medium text-sm flex items-center justify-center gap-2 bg-card hover:bg-brand/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand cursor-pointer"
      >
        <ListChecks className="w-4 h-4" aria-hidden="true" />
        {VOCI_BOTTONE}
      </button>
    );
  }
  if (stato === "errore") {
    return (
      <div className="space-y-2">
        <p className="text-sm flex items-start gap-2" role="alert">
          <TriangleAlert className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" aria-hidden="true" />
          {VOCI_ERRORE}
        </p>
        {voce.controlla && (
          <button
            type="button"
            onClick={voce.controlla}
            className="min-h-[44px] px-3 rounded-lg border border-border bg-card text-sm font-medium flex items-center gap-2 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
          >
            <RotateCcw className="w-4 h-4" aria-hidden="true" />
            {VOCI_RIPROVA}
          </button>
        )}
      </div>
    );
  }
  const d = voce.dettaglio;
  if (d?.motivo) {
    return (
      <p className="text-sm flex items-start gap-2">
        <FileX2 className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
        {motivoDettaglio(d.motivo)}
      </p>
    );
  }
  const righe = vociDaMostrare(d?.segnalazioni);
  // avvisi del gas (scritte approvate da Alfonso il 23/10): icona e testo, letti dagli screen reader
  const avvisi = (
    <>
      {/* ruling 67: come l'errore della luce, solo all'operatore */}
      {d?.ai_errore_gas && !cliente && (
        <p className="text-sm flex items-start gap-2" role="status">
          <TriangleAlert className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" aria-hidden="true" />
          {AI_ERRORE_GAS}
        </p>
      )}
      {d?.motivo_luce && (
        <p className="text-sm flex items-start gap-2" role="status">
          <FileX2 className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
          {avvisoLuce(d.motivo_luce)}
        </p>
      )}
    </>
  );
  if (righe.length === 0) {
    return (
      <div className="space-y-2">
        {avvisi}
        {/* l'analisi del gas non è riuscita: mai un «nessuna voce» che rassicura (ui-ux-pro-max: empty-states), nemmeno
            al cliente, che dal QR riceve solo il segno `gas_non_analizzato` (ruling 68) */}
        {!d?.ai_errore_gas && !d?.gas_non_analizzato && (
          <p className="text-sm flex items-start gap-2">
            <CircleCheck className="w-4 h-4 mt-0.5 text-brand-dark shrink-0" aria-hidden="true" />
            {VOCI_NESSUNA}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {avvisi}
      <ul className="bg-card rounded-lg border border-border divide-y divide-border">
        {righe.map((r, i) => <Riga key={`${r.prima.voce}-${r.prima.giudizio}-${i}`} riga={r} />)}
      </ul>
      <p className="text-xs text-muted-foreground">{VOCI_NOTA}</p>
    </div>
  );
}

/** Sezione della fase 2 sotto la spesa per la vendita: si vede solo se la fase 2 è prevista. */
export function SezioneVoci({ voce }: { voce: VoceAnalisi }) {
  const [estesa, setEstesa] = useState(false);
  const apri = useRef<HTMLButtonElement>(null);
  if (!voce.dettaglioStato) return null;
  const d = voce.dettaglio;
  const apribile = voce.dettaglioStato === "pronta" && !d?.motivo && (d?.famiglie?.some((f) => f.voci.length > 0) ?? false);
  const forniture = voce.risultato?.forniture ?? [];
  // l'intestazione dice la parte che la finestra mostra: la luce (anche nella dual, come oggi), il gas se la bolletta
  // è solo gas o se la luce non è stata analizzata
  const fornitura = (d?.motivo_luce ? undefined : forniture.find((f) => f.commodity === "luce"))
    ?? forniture.find((f) => f.commodity === "gas");
  return (
    <section aria-label={VOCI_TITOLO} className="border-t border-border pt-3 space-y-2">
      <h4 className="font-heading font-semibold text-[15px] flex items-center gap-2">
        <ListChecks className="w-4 h-4 text-brand" aria-hidden="true" />
        {VOCI_TITOLO}
      </h4>
      <Corpo voce={voce} />
      {apribile && d && (
        <>
          <button
            ref={apri}
            type="button"
            onClick={() => setEstesa(true)}
            className="w-full min-h-[44px] rounded-lg border border-border bg-card text-sm font-medium flex items-center justify-center gap-2 cursor-pointer hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
          >
            <Maximize2 className="w-4 h-4" aria-hidden="true" />
            {ESTESA_APRI}
          </button>
          {estesa && (
            <ModaleBolletta dettaglio={d} commodity={fornitura?.commodity ?? null} periodoDa={fornitura?.periodo_da ?? null}
              dual={forniture.some((f) => f.commodity === "luce") && forniture.some((f) => f.commodity === "gas")}
              periodoA={fornitura?.periodo_a ?? null}
              onClose={() => setEstesa(false)} ritorno={apri} />
          )}
        </>
      )}
    </section>
  );
}
